// AdvanceReport.js
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  Chip,
  Grid,
  TextField
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CustomCard from '../../../components/UIComponents/CustomCard';
import { useNavigate } from 'react-router-dom';
import { getReportData } from '../../../service/network';
import { AuthContext } from '../../../context/auth.context';
import { useLoading } from '../../../context/loading.context';
import BasicTable from '../../../components/Table';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { utils, writeFile } from 'xlsx';
// Status badge component for advance requests
const StatusBadge = ({ status, approval }) => {
  const statusConfig = {
    'Pending': { color: 'warning', label: 'Pending' },
    'Unpaid': { color: 'warning', label: 'Unpaid' },
    'Approved': { color: 'success', label: 'Approved' },
    'Returned': { color: 'success', label: 'Returned' },
    'Rejected': { color: 'error', label: 'Rejected' },
    'Paid': { color: 'info', label: 'Processed' },
    'Cancelled': { color: 'error', label: 'Cancelled' }
  };

  let config = statusConfig[status] || { color: 'default', label: status };
  if (approval && approval !== "Approved") {
    if (status !== "Draft") {
      config = statusConfig[approval] || { color: 'default', label: approval || status };
    }
  } else {
    config = statusConfig[status] || { color: 'default', label: status }
  }

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{
        fontWeight: 600,
        minWidth: '80px'
      }}
    />
  );
};

const getRows = (data) => {
  if (!data) return [];
  return data.map(item => ({
    ...item,
    status: <StatusBadge status={item.status} approval={item.custom_approval_status} />,
    advance_amount: `₹ ${parseFloat(item.advance_amount || 0).toFixed(2)}`,
    pending_amount: `₹ ${parseFloat((item.advance_amount || 0) - (item.paid_amount || 0) - (item.return_amount || 0)).toFixed(2)}`,
    requested_amount: `₹ ${parseFloat(item.advance_amount || 0).toFixed(2)}`
  }));
}

// Table configuration
const advanceRequestTableConfig = {
  rows: [
    {
      id: 'id',
      label: 'Request ID',
      flex: 1,
      minWidth: '120px',
      accessor: 'name',
      align: 'left',
      canSort: false
    },
    {
      id: 'employee',
      label: 'Employee',
      flex: 1.5,
      minWidth: '180px',
      accessor: 'employee_name',
      align: 'left',
      canSort: false
    },
    {
      id: 'purpose',
      label: 'Purpose',
      flex: 2,
      minWidth: '80px',
      accessor: 'purpose',
      align: 'left',
      canSort: false
    },
    {
      id: 'advance_amount',
      label: 'Advance Amount',
      flex: 1,
      minWidth: '150px',
      accessor: 'advance_amount',
      align: 'right',
      canSort: false
    },
    {
      id: 'pending_amount',
      label: 'Pending Amount',
      flex: 1,
      minWidth: '150px',
      accessor: 'pending_amount',
      align: 'right',
      canSort: false
    },
    {
      id: 'requested_amount',
      label: 'Requested Amount',
      flex: 1,
      minWidth: '150px',
      accessor: 'requested_amount',
      align: 'right',
      canSort: false
    },
    {
      id: 'status',
      label: 'Status',
      flex: 1,
      minWidth: '120px',
      accessor: 'status',
      align: 'center',
      canSort: false
    },
    {
      id: 'approvedBy',
      label: 'Approver',
      flex: 1.2,
      minWidth: '150px',
      accessor: 'custom_approver',
      align: 'left',
      canSort: false
    }
  ]
};

export default function AdvanceReport() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const { selLocation } = useContext(AuthContext);
  const [reqs, setReqs] = useState([]);
  const [filteredReqs, setFilteredReqs] = useState([]);

  // Initialize with current month range
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const [filters, setFilters] = useState({
    from_date: moment().startOf('month').format('YYYY-MM-DD'),
    to_date: moment().format('YYYY-MM-DD'),
    company: selLocation && selLocation[0] ? selLocation[0] : ''
  });

  const handleDateChange = (field, date) => {
    setFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleCreateRequest = () => {
    navigate('/advance-requests/create');
  };

  const applyFilters = () => {
    if (!reqs) return [];

    return reqs.filter(request => {
      const matchesSearch =
        !searchQuery ||
        (request.name && request.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (request.employee_name && request.employee_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (request.purpose && request.purpose.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (request.advance_amount && request.advance_amount.toString().includes(searchQuery)) ||
        (request.custom_approver && request.custom_approver.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesSearch;
    });
  };
  const exportToExcel = () => {
    // Prepare data for export (remove React components)
    const exportData = filteredReqs.map(item => ({
      ...item,
      status: item.status?.props?.approval || item.status?.props?.status || item.status,
      advance_amount: item.advance_amount,
      pending_amount: item.pending_amount,
      requested_amount: item.requested_amount
    }));

    // Create worksheet
    const ws = utils.json_to_sheet(exportData);

    // Create workbook
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "AdvanceRequests");

    // Generate filename with current date
    const fileName = `Advance_Requests_${moment().format('YYYY-MM-DD')}.xlsx`;

    // Export to Excel
    writeFile(wb, fileName);
  };
  const fetchAdvancesList = async () => {
    try {
      showLoader();

      const payload = {
        doctype: "Employee Advance",
        fields: [
          "name",
          "employee",
          "employee_name",
          "purpose",
          "status",
          "advance_amount",
          "posting_date",
          "paid_amount",
          "return_amount",
          "custom_approver",
          "custom_approval_status",
          "department",
          "company"
        ],
        filters: [
          ["company", "=", filters.company],
          ["posting_date", ">=", filters.from_date],
          ["posting_date", "<=", filters.to_date]
        ],
        order_by: 'posting_date desc',
        page_length: 2000
      };

      const response = await getReportData(payload);
      console.log('API Response:', response);

      let advances = [];
      if (response?.data?.message?.values) {
        advances = response.data.message.values.map(valueArray => {
          const obj = {};
          response.data.message.keys.forEach((key, index) => {
            obj[key] = valueArray[index] || (key.includes('amount') ? 0 : '-');
          });
          return obj;
        });
      } else if (Array.isArray(response?.data?.message)) {
        advances = response.data.message;
      }

      setReqs(advances);
      setFilteredReqs(advances);

    } catch (err) {
      console.error('Error fetching advance list:', err);
      setReqs([]);
      setFilteredReqs([]);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (filters.company) {
      fetchAdvancesList();
    }
  }, [filters.company, filters.from_date, filters.to_date]);

  useEffect(() => {
    const filtered = applyFilters();
    setFilteredReqs(filtered);
  }, [reqs, searchQuery]);

  return (
    <Box
      sx={{
        width: '100%',
        p: { xs: 1, sm: 2 },
        boxSizing: 'border-box'
      }}
    >
      <CustomCard
        bgColor="light"
        shadow="lg"
        borderRadius="xxl"
        sx={{
          width: '100%',
          minHeight: 800,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="subTitle" color='primary.main' >
              Advance Requests Report

            </Typography>
            <Typography variant="body1" color="text.secondary">
              Overview of Advance Requests Report

            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={exportToExcel}

              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                px: 3
              }}
            >
              Export  Data
            </Button>
          </Box>
        </Box>

        {/* Filter Section */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Grid item xs={12} sm={3}>
            <StyledTextField
              label="From Date"
              type="date"
              value={filters.from_date}
              onChange={(e) => handleDateChange('from_date', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon color="action" />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <StyledTextField
              label="From Date"
              type="date"
              value={filters.to_date}
              onChange={(e) => handleDateChange('to_date', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon color="action" />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <StyledTextField
              label="Company"
              value={filters.company}
              size="small"
              fullWidth
              disabled
            />
          </Grid>


        </Box>

        <Box sx={{ flex: 1 }}>
          <BasicTable
            data={{
              rows: advanceRequestTableConfig.rows,
              columns: getRows(filteredReqs)
            }}
            onRowClick={(rowData) => console.log('Row clicked:', rowData)}
            backgroundColor="#f9fafc"
          />
        </Box>
      </CustomCard>
    </Box>
  );
}